import { Link } from "react-router-dom";
import {
    ArrowRight,
    Smartphone,
    QrCode,
    Printer,
    Sparkles,
    Clock,
    ShieldCheck,
    Package,
    Heart,
    Gift,
    Users,
    CreditCard,
    Building2,
    Plane,
    ShoppingBag,
    Magnet,
    Zap,
    Backpack,
} from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { SITE_IMAGES } from "@/lib/siteAssets";

const FAQS = [
    {
        q: "How long does it take to print a phone case?",
        a: "About 3 minutes from the moment a customer uploads their image at the machine to a finished, ready-to-use case.",
    },
    {
        q: "What phone models are supported?",
        a: "61+ phone models and variations across iPhone, Samsung Galaxy, Google Pixel and more — including all the Plus / Pro / Ultra / Max variants. The machine displays an up-to-date list on-screen.",
    },
    {
        q: "What kinds of images can be printed?",
        a: "Anything a customer has on their phone — photos, screenshots, artwork, memes, business logos. If it's an image, it can go on a case. Your only limit is your imagination.",
    },
    {
        q: "How do customers pay at the machine?",
        a: "All Vendagain phone case machines are 100% cashless — tap, card, Apple Pay, Google Pay. No cash handling, no coin jams, no theft risk.",
    },
    {
        q: "Do the cases actually protect the phone?",
        a: "Yes — snug full-body cases with precise cutouts, raised camera bezel and premium button feel. Everyday drop and scratch protection. They're not marketed as tough cases though — if you're chasing military-grade shock protection, look at a dedicated tough case.",
    },
    {
        q: "What sites are ideal for a phone case machine?",
        a: "High foot-traffic locations only — major shopping centres, malls, and airports. These are premium machines with significant capital investment behind them, and they perform best where thousands of people walk past every day.",
    },
    {
        q: "Do you offer magnetic cases?",
        a: "Yes — our premium Magnetic Experience cases are available on selected phone models. They snap perfectly onto magnetic stands, car holders and wireless chargers, and give you flawless MagSafe-style alignment for wallets, grips and accessories. Look for the magnet icon at the machine to see which models are supported.",
    },
    {
        q: "How does the commercial arrangement work?",
        a: "We lease floor space from the venue on commercial terms. Every site is priced differently depending on location, foot traffic and space. Get in touch for a specific proposal.",
    },
];

export default function PhoneCaseVendingPage() {
    useSeo({
        title: "Phone Case Vending Machines Perth — Print Any Image in Minutes",
        description:
            "Vendagain's DIY phone case printing vending machines let customers print any image from their phone onto a premium case in about 3 minutes. 61+ phone models, magnetic upgrade available, 100% cashless. Available for premium malls, shopping centres and airports.",
        canonicalPath: "/phone-case-vending",
        keywords:
            "phone case vending machine Perth, phone case printing Perth, custom phone case Perth, magnetic phone case Perth, personalised phone case Perth, phone case machine airport, phone case machine shopping centre, phone case vending Australia",
        jsonLd: [
            {
                "@context": "https://schema.org",
                "@type": "Product",
                name: "Custom Phone Case (Vendagain)",
                description:
                    "Personalised phone case printed on-demand at a Vendagain vending machine.",
                brand: { "@type": "Brand", name: "Vendagain" },
                category: "Phone accessory",
            },
            {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: FAQS.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
            },
        ],
    });

    return (
        <>
            {/* HERO */}
            <section className="pt-12 lg:pt-20 pb-14 lg:pb-24 border-b border-neutral-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            Custom Phone Case Vending
                        </span>
                        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]" data-testid="pcv-hero-title">
                            Your only limit<br />
                            is your<br />
                            <span className="text-green-600">imagination.</span>
                        </h1>
                        <p className="mt-5 text-lg text-neutral-600 leading-relaxed max-w-xl">
                            A DIY vending machine that prints any image from a
                            customer's phone onto a premium case — in about
                            3 minutes. No app. No account. 100% cashless.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/designer" className="vg-pill vg-pill-primary" data-testid="pcv-cta-designer">
                                <Smartphone size={16} />
                                Preview a design
                                <ArrowRight size={14} />
                            </Link>
                            <Link to="/host-a-machine" className="vg-pill vg-pill-outline">
                                Lease a site to us
                            </Link>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="relative aspect-[3/4] rounded-3xl overflow-hidden bg-neutral-100 border border-neutral-200">
                            <img
                                src={SITE_IMAGES.caseMachineAngle}
                                alt="Vendagain DIY phone case printing vending machine at Westfield"
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="eager"
                                data-testid="pcv-hero-image"
                            />
                            <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/60 backdrop-blur text-green-400 text-[11px] font-semibold uppercase tracking-widest">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                                Live at Westfield WA
                            </div>
                            <div className="absolute bottom-4 left-4 right-4 bg-black/70 backdrop-blur text-white rounded-2xl p-4 flex items-baseline gap-2">
                                <span className="text-4xl font-semibold leading-none tracking-tight">3</span>
                                <span className="text-green-400 text-lg font-semibold">min</span>
                                <span className="text-neutral-300 text-xs ml-2 flex-1">
                                    From upload to a finished case in the customer's hand.
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-12">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            How it works
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Simple Steps. No app required.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <StepCard n="01" title="Hit Customize Now" body="At the machine, tap the big Customize Now button to start your case." />
                        <StepCard n="02" title="Choose Magnetic (optional)" body="Want the Magnetic Experience? Select it at the top of the phone-choice page before picking your model." />
                        <StepCard n="03" title="Choose your phone model" body="iPhone options on the right; Samsung & Google options on the left side of the screen." />
                        <StepCard n="04" title="Scan the QR code" body="Point your phone camera at the QR code shown on the screen to open the uploader." />
                        <StepCard n="05" title="Select a photo" body="Pick any image from your phone's camera roll. Anything you can imagine." />
                        <StepCard n="06" title="Adjust the fit" body="Move, resize and rotate your image on-screen. Watch the camera cutout so you don't lose anything important." />
                        <StepCard n="07" title="Hit Print" body="Happy with it? Confirm and send it to print." />
                        <StepCard n="08" title="Tap to pay" body="Tap your card or phone on the payment device. 100% cashless." />
                        <StepCard n="09" title="Collect your case" body="About 3 minutes later, collect your brand new custom phone case from the machine." />
                    </div>
                </div>
            </section>

            {/* THE MACHINE — real photos */}
            <section className="py-16 lg:py-24 bg-neutral-50 border-y border-neutral-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-10">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            The machine, in the wild
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Case Laboratory by Vendagain.
                        </h2>
                        <p className="mt-3 text-neutral-600 leading-relaxed max-w-xl">
                            Bright, unmissable, and already turning heads at
                            Westfield. This is the actual machine your customers
                            will interact with — clean, modern, and clearly
                            branded.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <figure className="rounded-3xl overflow-hidden bg-neutral-100 border border-neutral-200 aspect-[3/4]">
                            <img
                                src={SITE_IMAGES.caseMachineFront}
                                alt="Vendagain Case Laboratory phone case vending machine — front view"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </figure>
                        <figure className="rounded-3xl overflow-hidden bg-neutral-100 border border-neutral-200 aspect-[3/4]">
                            <img
                                src={SITE_IMAGES.caseMachineAngle}
                                alt="Vendagain Case Laboratory phone case vending machine — angled view showing sample cases"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </figure>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section className="py-16 lg:py-24 bg-neutral-50 border-y border-neutral-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-12">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            Features
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Every case is unique. Every machine is premium.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <FeatureCard icon={<Package size={20} />} title="61+ phone models & variations" body="Current iPhones, Galaxies, Pixels — plus their Plus / Pro / Ultra / Max variations. Updated as new phones launch." />
                        <FeatureCard icon={<Sparkles size={20} />} title="300 DPI print" body="High-resolution, vibrant, edge-to-edge printing. Photos look sharp, illustrations pop." />
                        <FeatureCard icon={<ShieldCheck size={20} />} title="Quality feel, perfect fit" body="Snug full-body case, precise cutouts, quality button feel every time. Everyday protection, not a tough case." />
                        <FeatureCard icon={<Clock size={20} />} title="About 3 minutes" body="From tap to finished case. Faster than the queue at your favourite coffee spot." />
                        <FeatureCard icon={<CreditCard size={20} />} title="100% cashless" body="Tap, card, Apple Pay, Google Pay. No cash handling. No coin jams. No theft risk." />
                        <FeatureCard icon={<Heart size={20} />} title="Truly one-of-a-kind" body="Every case is genuinely unique — no design is ever printed twice unless the customer wants it to be." />
                    </div>
                </div>
            </section>

            {/* USE CASES */}
            <section className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-12">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            What people print
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            If it's on your phone, it can be on your case.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <UseCaseCard icon={<Gift size={20} />} title="Last-minute gifts" body="Birthdays, anniversaries, Father's Day — a case made from a favourite photo hits harder than anything wrapped in plastic." />
                        <UseCaseCard icon={<Heart size={20} />} title="Milestone memories" body="Wedding photos, a baby's first picture, an unforgettable holiday — carry it with you every day." />
                        <UseCaseCard icon={<Users size={20} />} title="Team & branding" body="Sports clubs, small businesses, and community groups can print their logo on cases for members." />
                    </div>
                </div>
            </section>

            {/* MAGNETIC UPGRADE — premium tier marketing */}
            <section className="py-16 lg:py-24 bg-gradient-to-br from-neutral-900 via-neutral-950 to-black text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden="true"
                    style={{
                        backgroundImage: "radial-gradient(circle at 20% 30%, rgba(34,197,94,0.25), transparent 55%), radial-gradient(circle at 80% 70%, rgba(34,197,94,0.15), transparent 45%)",
                    }}
                />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
                    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-start">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/15 text-green-400 text-[11px] font-semibold uppercase tracking-widest mb-5 border border-green-500/20">
                                <Magnet size={12} />
                                Premium tier
                            </div>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05] text-white">
                                Upgrade to the<br />
                                <span className="text-green-400">Magnetic Experience.</span>
                            </h2>
                            <p className="mt-5 text-neutral-300 text-lg leading-relaxed max-w-xl">
                                Level up your custom case with our premium
                                magnetic technology. Perfectly aligned for your
                                fast-paced lifestyle — and yes, it still prints
                                anything you can imagine.
                            </p>
                            <p className="mt-4 text-neutral-500 text-sm max-w-xl">
                                Available on selected phone models at Vendagain
                                machines. Look for the magnet icon at the machine.
                            </p>
                        </div>
                        <div className="space-y-3">
                            <MagBenefit
                                icon={<Magnet size={20} />}
                                title="Snap & Go"
                                body="Flawless compatibility with all magnetic stands and car holders."
                            />
                            <MagBenefit
                                icon={<Zap size={20} />}
                                title="Optimised Charging"
                                body="Perfect alignment for fast, uninterrupted wireless power."
                            />
                            <MagBenefit
                                icon={<Backpack size={20} />}
                                title="Accessory Ready"
                                body="Easily attach your favourite snap-on wallets and grips."
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* IDEAL VENUES — for site partnerships */}
            <section className="py-16 lg:py-24 bg-neutral-950 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-16 items-start">
                        <div>
                            <span className="text-xs font-semibold uppercase tracking-widest text-green-400">
                                Own a premium venue?
                            </span>
                            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight text-white">
                                We want to be there.
                            </h2>
                            <p className="mt-4 text-neutral-300 leading-relaxed max-w-xl">
                                Our phone case machines are a significant capital
                                investment and perform best in premium
                                high-footfall locations. If you manage a major
                                shopping centre, mall or airport, we'd love to
                                talk about a leasing arrangement.
                            </p>
                            <div className="mt-6">
                                <Link to="/host-a-machine#form" className="vg-pill vg-pill-accent">
                                    Talk to our team
                                    <ArrowRight size={14} />
                                </Link>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <VenueCard icon={<ShoppingBag size={18} />} title="Shopping centres" />
                            <VenueCard icon={<Building2 size={18} />} title="Major malls" />
                            <VenueCard icon={<Plane size={18} />} title="Airports" />
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-16 lg:py-24 bg-neutral-50 border-t border-neutral-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="mb-10 text-center">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            FAQ
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Common questions
                        </h2>
                    </div>
                    <div className="space-y-3">
                        {FAQS.map((f, i) => (
                            <details
                                key={f.q}
                                className="group bg-white border border-neutral-200 rounded-2xl p-5 [&_summary::-webkit-details-marker]:hidden"
                                data-testid={`faq-item-${i}`}
                            >
                                <summary className="cursor-pointer flex items-start justify-between gap-3 text-neutral-900 font-medium text-[15px]">
                                    <span>{f.q}</span>
                                    <span className="text-green-500 text-2xl leading-none group-open:rotate-45 transition-transform">
                                        +
                                    </span>
                                </summary>
                                <p className="mt-3 text-neutral-600 text-sm leading-relaxed">
                                    {f.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 lg:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
                        Ready to see your design?
                    </h2>
                    <p className="mt-4 text-neutral-600 max-w-lg mx-auto">
                        Play with your image in our free preview tool before
                        you head to the machine.
                    </p>
                    <div className="mt-8">
                        <Link to="/designer" className="vg-pill vg-pill-primary">
                            Open the Case Designer
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}

function StepCard({ n, title, body }) {
    return (
        <div className="bg-white border border-neutral-200 rounded-2xl p-6 h-full">
            <span className="font-mono text-xs text-neutral-400 block mb-3">
                {n}
            </span>
            <h3 className="text-base font-semibold mb-2 leading-tight">{title}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{body}</p>
        </div>
    );
}

function FeatureCard({ icon, title, body }) {
    return (
        <div className="bg-white border border-neutral-200 rounded-2xl p-6">
            <div className="w-10 h-10 rounded-lg bg-neutral-950 text-white flex items-center justify-center mb-4">
                {icon}
            </div>
            <h3 className="text-lg font-semibold mb-2">{title}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{body}</p>
        </div>
    );
}

function UseCaseCard({ icon, title, body }) {
    return (
        <div className="bg-neutral-950 text-white rounded-2xl p-6">
            <div className="w-10 h-10 rounded-lg bg-green-500 text-white flex items-center justify-center mb-4">
                {icon}
            </div>
            <h3 className="text-lg font-semibold mb-2" dangerouslySetInnerHTML={{ __html: title }} />
            <p className="text-sm text-neutral-300 leading-relaxed">{body}</p>
        </div>
    );
}

function VenueCard({ icon, title }) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-green-500 text-white flex items-center justify-center flex-shrink-0">
                {icon}
            </div>
            <span className="text-sm font-medium text-white">{title}</span>
        </div>
    );
}

function MagBenefit({ icon, title, body }) {
    return (
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 flex items-start gap-4 hover:border-green-500/40 transition-colors">
            <div className="w-11 h-11 rounded-lg bg-green-500 text-white flex items-center justify-center flex-shrink-0">
                {icon}
            </div>
            <div>
                <h3
                    className="text-base font-semibold text-white mb-1"
                    dangerouslySetInnerHTML={{ __html: title }}
                />
                <p className="text-sm text-neutral-400 leading-relaxed">{body}</p>
            </div>
        </div>
    );
}
